/**
 * Rest timer.
 *
 * A plain foreground interval is the real mechanism while the app is open, and
 * this component stays the source of truth for the number on screen. It is not
 * the source of truth for *when you find out*: with the screen off the WebView
 * timers freeze, so the OS alarm is the only thing that can tell you. That is
 * why the app holds USE_EXACT_ALARM — see native/notify.ts. Believing the
 * foreground timer made the alarm unimportant is what left it ~31s late.
 *
 * The LocalNotification is the backgrounded-app backup, and it is scheduled at
 * the *start* of the rest, not at the end. That direction is the whole point:
 * with the screen off Android freezes WebView timers, so the tick that would
 * notice "we hit zero" never runs. Handing the deadline to the OS up front is
 * the only version of this that survives a locked phone.
 *
 * Remounted with a new `key` on each logged set, which restarts it — that
 * remount is what `autoStart` rides on. On the first mount of a fresh workout
 * it is false, so the timer sits idle at full duration with a Start button
 * rather than counting down at someone who has not lifted anything yet.
 */

import { useCallback, useEffect, useRef, useState } from 'react'

import { cancelRestDone, scheduleRestDone } from '../native/notify'
import { shiftDeadline } from '../lib/restShift'

export function RestTimer({
  seconds,
  autoStart = false,
}: {
  seconds: number
  autoStart?: boolean
}) {
  const [remaining, setRemaining] = useState(seconds)
  const [running, setRunning] = useState(autoStart)
  // Wall-clock deadline, not a decrementing counter: setInterval drifts badly
  // when a phone throttles background timers, and "2:00 rest" that actually
  // ran 2:40 is worse than no timer.
  const deadline = useRef<number | null>(null)

  const start = useCallback(() => {
    setRemaining(seconds)
    setRunning(true)
    deadline.current = Date.now() + seconds * 1000
    // The OS alarm rides along with any real start, whichever way it began.
    // It used to be gated on a separate flag because the timer armed itself on
    // mount; now that opening the screen doesn't start anything, "the timer is
    // running" is the only condition that matters.
    void scheduleRestDone(seconds)
  }, [seconds])

  /** Move the deadline by `delta` seconds. Arithmetic and its edge cases in
   *  lib/restShift, where they can be tested without a ref and an interval. */
  const shift = useCallback((delta: number) => {
    const next = shiftDeadline({ deadline: deadline.current, now: Date.now(), delta })

    // Subtracting past the end is a deliberate "I'm done resting" — the same
    // thing Skip means, so it does the same thing, silently. Someone watching
    // the screen and pressing a button does not need the phone to buzz at them
    // about the outcome.
    if (next.kind === 'end') {
      deadline.current = null
      setRunning(false)
      setRemaining(0)
      void cancelRestDone()
      return
    }

    deadline.current = next.deadline
    // From the real deadline, not `r + delta`: on a timer that had already
    // expired those two disagree, and the display would show a total it wasn't
    // counting down.
    setRemaining(next.remaining)
    setRunning(true)
    // The OS alarm moves with it, or the backup contradicts the number on
    // screen — firing early on a subtraction, late on an addition.
    void scheduleRestDone(next.remaining)
  }, [])

  useEffect(() => {
    setRemaining(seconds)
    // Opening a fresh workout is not the same as finishing a set. Arming here
    // put the clock in mid-countdown before the first rep was logged, so the
    // first real rest was always short by however long the setup took.
    if (!autoStart) {
      setRunning(false)
      deadline.current = null
      return
    }
    start()
  }, [seconds, autoStart, start])

  useEffect(() => {
    if (!running) return
    const tick = () => {
      if (deadline.current === null) return
      const left = Math.max(0, Math.round((deadline.current - Date.now()) / 1000))
      setRemaining(left)
      if (left === 0) {
        setRunning(false)
        notifyDone()
      }
    }
    tick()
    const id = setInterval(tick, 250)
    return () => clearInterval(id)
  }, [running])

  // Idle = never started and still at full duration. Distinct from finished,
  // which sits at 0:00 — the fill bar and the controls differ between them.
  const idle = !running && remaining === seconds
  const pct = seconds > 0 && !idle ? ((seconds - remaining) / seconds) * 100 : 0

  return (
    <div
      className="card"
      // Read by the Log screen to find out how much of the top of the screen this
      // is covering while pinned. Deliberately an attribute and not a forwarded
      // ref through a wrapper element: a wrapper would become this card's
      // containing block, and `position: sticky` can only stick within its
      // containing block — so the pinning above would silently stop working.
      data-rest-timer=""
      style={{
        padding: '.7rem .9rem',
        margin: '.75rem 0',
        overflow: 'hidden',

        // Pinned while counting. Mid-workout you are scrolling the queue and
        // the set list to decide what's next, and the one number you need is
        // the one that was scrolled off the top.
        //
        // Sticky rather than fixed, deliberately: a sticky element keeps its
        // space in the flow, so arming the timer can't reflow the page under a
        // thumb that's already reaching for +15s, and the card sits inside
        // .wrap so it stays aligned with the content it belongs to instead of
        // spanning the full width like a system bar.
        //
        // `relative` when idle or finished — same containing block for the fill
        // below, no pin. Rest being over is exactly when the top of the screen
        // should go back to the workout.
        position: running ? 'sticky' : 'relative',

        // Below the notch, not behind it. body carries this inset as padding,
        // but a sticky offset is measured from the viewport, which that padding
        // does not move — so it has to be stated again here.
        top: running ? 'env(safe-area-inset-top, 0px)' : undefined,

        // Above the page content it now overlaps, and below nothing: the tab bar
        // is fixed to the opposite edge and can never meet this.
        zIndex: running ? 2 : undefined,

        // The card is opaque, but without an edge the content sliding under it
        // reads as part of it. Only while pinned, or the idle timer looks like
        // it's floating over a page it's actually sitting in.
        boxShadow: running ? '0 2px 8px rgb(0 0 0 / 18%)' : undefined,
      }}
    >
      <div
        aria-hidden
        style={{
          position: 'absolute',
          inset: 0,
          width: `${pct}%`,
          background: 'var(--surface-2)',
          transition: 'width .25s linear',
        }}
      />
      <div className="row" style={{ position: 'relative' }}>
        <span>
          <span className="muted">Rest </span>
          <strong style={{ fontVariantNumeric: 'tabular-nums' }}>
            {formatMMSS(remaining)}
          </strong>
        </span>
        {/* Three controls now instead of two, next to a clock, inside a card,
            inside .wrap. That fits a 360px phone with room to spare and a 320px
            one without — so it wraps rather than overflowing, which is the one
            failure mode that would push a button off the card edge. */}
        <span style={{ display: 'flex', gap: '.4rem', flexWrap: 'wrap', justifyContent: 'flex-end' }}>
          {idle ? (
            <button
              className="secondary"
              style={{ marginTop: 0, padding: '.25rem .6rem', minHeight: 0, fontSize: '.85rem',
                  whiteSpace: 'nowrap' }}
              onClick={start}
            >
              Start
            </button>
          ) : (
            <>
              <button
                className="secondary"
                aria-label="Take 15 seconds off the rest"
                // Nothing left to take off. Pressing it would just re-end an
                // already-ended rest, so it says so instead of pretending.
                disabled={remaining === 0}
                style={{ marginTop: 0, padding: '.25rem .6rem', minHeight: 0, fontSize: '.85rem',
                  whiteSpace: 'nowrap' }}
                onClick={() => shift(-15)}
              >
                −15s
              </button>
              <button
                className="secondary"
                aria-label="Add 15 seconds to the rest"
                style={{ marginTop: 0, padding: '.25rem .6rem', minHeight: 0, fontSize: '.85rem',
                  whiteSpace: 'nowrap' }}
                onClick={() => shift(15)}
              >
                +15s
              </button>
            </>
          )}
          {!idle && (
            <button
              className="secondary"
              style={{ marginTop: 0, padding: '.25rem .6rem', minHeight: 0, fontSize: '.85rem',
                  whiteSpace: 'nowrap' }}
              onClick={() => {
                setRunning(false)
                setRemaining(0)
                void cancelRestDone()
              }}
            >
              Skip
            </button>
          )}
        </span>
      </div>
    </div>
  )
}

function formatMMSS(total: number): string {
  const m = Math.floor(total / 60)
  const s = total % 60
  return `${m}:${String(s).padStart(2, '0')}`
}

/**
 * Best-effort end-of-rest signal on the web. In the APK this is replaced by a
 * Capacitor LocalNotification, which works with the screen off.
 */
function notifyDone() {
  try {
    if ('vibrate' in navigator) navigator.vibrate([200, 100, 200])
  } catch {
    /* not supported — the visual timer is still the primary signal */
  }
  try {
    if ('Notification' in window && Notification.permission === 'granted') {
      new Notification('Rest over', { body: 'Next set.', silent: false })
    }
  } catch {
    /* notifications blocked; ignore */
  }
}
